import {
  useState,
  useRef,
  Children,
  cloneElement,
  type ReactNode,
  type SyntheticEvent,
  isValidElement,
} from 'react'
import styled from 'styled-components'
import { Dropdown } from '@/features/shared/components/Dropdown'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import { type OptionProps } from '@/features/shared/components/Dropdown/Option'
import { HelperText } from '@/features/shared/elements/Inputs/styles'
import { VisuallyHidden } from '@/features/shared/elements/VisuallyHidden'
import { ChevronDownIcon, ChevronUpIcon, PositiveIcon } from '@shipt/design-system-icons'
import { typographyAttributes, Body } from '@shipt/design-system-typography'

type Value = string | number
type Props = {
  id: string
  value: Value
  onChange?: (value: string) => void
  labelText?: string
  isLabelHidden?: boolean
  className?: string
  children: ReactNode
  buttonType?: 'button'
  disabled?: boolean
  shouldScaleUp?: boolean
  error?: string
  isPII?: boolean
}

export const Select = ({
  id,
  value,
  onChange,
  labelText = '',
  isLabelHidden = false,
  children,
  className,
  buttonType,
  disabled,
  shouldScaleUp,
  isPII = false,
  error,
}: Props) => {
  const [isDropdownShown, setIsDropdownShown] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const handleButtonClick = (e: SyntheticEvent) => {
    e.preventDefault()
    setIsDropdownShown(!isDropdownShown)
  }
  const handleChange = (newValue: string) => {
    onChange?.(newValue)
  }
  const handleClose = () => {
    buttonRef.current?.focus()
    setIsDropdownShown(false)
  }

  const selectedOptionLabel = _findLabel(children, value)
  const selectedOptionLabelId = `${id}-selected-option-label`
  const selectLabelId = `${id}-label`
  const hasError = Boolean(error)
  return (
    <Wrapper>
      {!!labelText && !!isLabelHidden && (
        <VisuallyHidden id={selectLabelId}>{labelText}</VisuallyHidden>
      )}
      {!!labelText && !isLabelHidden && (
        <LabelText
          shouldScaleUp={shouldScaleUp}
          as="label"
          id={selectLabelId}
          {...typographyAttributes({ isPII })}
          size="sm"
          variant="secondary"
        >
          {labelText}
        </LabelText>
      )}
      <InternalWrapper>
        <SelectButton
          disabled={disabled}
          data-is-open={isDropdownShown}
          ref={buttonRef}
          onClick={handleButtonClick}
          aria-labelledby={`${selectLabelId} ${selectedOptionLabelId}`}
          aria-haspopup="listbox"
          aria-expanded={isDropdownShown}
          data-has-error={hasError}
          {...typographyAttributes({ isPII })}
          {...(buttonType && { type: buttonType })}
        >
          <TextWrapper>
            <SelectedAddress id={selectedOptionLabelId} size="md" align="left">
              {selectedOptionLabel}
            </SelectedAddress>
            {!disabled && (
              <Chevron>
                {isDropdownShown ? <ChevronUpIcon size="md" /> : <ChevronDownIcon size="md" />}
              </Chevron>
            )}
          </TextWrapper>
        </SelectButton>
        <HelperTextWrapper>
          {!!hasError && (
            <HelperText
              {...typographyAttributes({ isPII })}
              aria-live="assertive"
              id={`${id}-error`}
              hasError
            >
              {error}
            </HelperText>
          )}
        </HelperTextWrapper>
        <AddressDropdown
          {...typographyAttributes({ isPII })}
          isOpen={isDropdownShown}
          onClose={handleClose}
          onChange={handleChange}
          value={value}
          buttonRef={buttonRef}
          isScrollable
          className={className}
        >
          {Children.map(children, (child) => {
            if (isValidElement<{ hidden: boolean }>(child) && child.props.hidden) {
              return null
            }
            if (child && isValidElement<OptionProps<Value>>(child) && child.props.value) {
              const selected = child.props.value === value
              return cloneElement(
                child,
                { selected },
                renderSelectionIndicator(selected),
                child.props.children,
              )
            }
            return child
          })}
        </AddressDropdown>
      </InternalWrapper>
    </Wrapper>
  )
}

type ChildProps = {
  value?: number | string
  label?: string
  children?: string
}

const _findLabel = (nodes: ReactNode, value: string | number) => {
  const children = Children.toArray(nodes)
  const foundOption = children.find(
    (child) => isValidElement<ChildProps>(child) && child.props.value === value,
  )
  if (foundOption && isValidElement<ChildProps>(foundOption)) {
    const { label, children: childrenOptions } = foundOption.props
    return label || childrenOptions
  }
  const firstChild = isValidElement<ChildProps>(children[0]) && children[0]
  return firstChild && (firstChild.props.label || firstChild.props.children)
}

const renderSelectionIndicator = (selected: boolean): ReactNode => {
  return (
    <CheckMarkWrapper selected={selected}>
      {!!selected && <PositiveIcon surface="inverse" size="sm" />}
    </CheckMarkWrapper>
  )
}

const AddressDropdown = styled(Dropdown)`
  margin-top: 0.625rem;
  border-radius: 1rem;
  box-shadow: ${({ theme }) => theme.shadows[2]};
  max-height: 10rem;
`

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.color.background.base.neutral.default};
`

const LabelText = styled(Body)`
  margin-bottom: 0.25rem;
  font-weight: 600;
  display: block;
  cursor: default;
`

const InternalWrapper = styled.div`
  position: relative;
`

export const SelectButton = styled(TransparentButton)<{
  'data-has-error': boolean
  'data-is-open': boolean
}>`
  padding-left: 0.2rem;
  padding-right: 0.5rem;
  color: ${({ theme }) => theme.color.border.base.neutral.subtle};
  width: 100%;
  border-radius: 1.5rem;
  border: 1px solid ${({ theme }) => theme.color.border.action.bold};
  height: 2.5rem;
  outline-color: currentcolor;

  &[data-has-error='true'] {
    border-color: ${({ theme }) => theme.color.border.feedback.negative.loud};
  }
`

const TextWrapper = styled.div`
  justify-content: space-between;
  align-items: center;
  display: flex;
`

const SelectedAddress = styled(Body)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  justify-content: flex-start;
  margin-left: 0.75rem;
`

const Chevron = styled.div`
  justify-content: flex-end;
  align-items: center;
  flex-direction: column;
  display: flex;
  font-weight: 600;
`

const CheckMarkWrapper = styled.div<{
  selected: boolean
}>`
  left: 0.5rem;
  height: 1.375rem;
  width: 1.375rem;
  color: ${({ theme }) => theme.color.background.base.neutral.default};
  position: absolute;
  justify-content: center;
  align-items: center;
  display: flex;
  border-radius: 100%;
  background-color: ${({ selected, theme }) =>
    selected ? theme.color.background.base.brand.primary : 'transparent'};
  border: ${({ selected, theme }) =>
    selected ? `2px solid ${theme.color.border.base.neutral.loud_active}` : 'none'};
`

const HelperTextWrapper = styled.div`
  position: absolute;
`
